import { useMsal } from '@azure/msal-react';
import { signOut } from '../auth/authSession';

/**
 * Access denied — Bug #19757.
 *
 * Shown INSTEAD of the app shell to an account that is signed in to the tenant but holds no role on
 * XCentral (an empty `roles` claim, or Entra refusing the API token with AADSTS50105). Nothing else
 * renders: no nav, no data, no API calls — the only thing disclosed is the signed-in identity, which
 * the user already knows (ISO 27001 A.8.3).
 *
 * The one action SIGNS OUT (`logoutRedirect`). It must never be `loginRedirect`: Entra recognises
 * the account, so a login would re-authenticate silently and land straight back here — a loop.
 * Signing out clears the session and lets the user pick another account at Microsoft's picker.
 *
 * Not for "wrong role for this page" — a known user opening an Owner-only screen keeps the page's
 * own denial and stays signed in.
 */
export function NoAccess({ upn }: { upn?: string }) {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const identity = upn ?? account?.username ?? '';

  const handleSignOut = () => {
    // #24685 — clears local MSAL state (a stuck interaction marker would make logoutRedirect
    // refuse to start), signs out the active account with its logout hint, and falls back to the
    // landing page on failure. Never rejects unhandled.
    void signOut(instance, account ?? null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <img src="/brand/conscierra-icon-fern.png" alt="" aria-hidden="true" className="h-9 w-9" />
          <div>
            <p className="text-base font-semibold text-gray-900">Conscierra</p>
            <p className="text-xs uppercase tracking-[0.18em] text-gray-500">Admin Portal</p>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center">
        <div className="mx-auto w-full max-w-lg px-4 py-16 sm:px-6">
          <div className="rounded-lg border border-gray-200 bg-white px-6 py-8 shadow-sm text-center">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Access denied</h1>
            <p className="mt-4 text-sm text-gray-600">You are signed in as</p>
            {identity ? (
              <p className="mt-1 break-all text-base font-semibold text-gray-900">{identity}</p>
            ) : (
              <p className="mt-1 text-base font-semibold text-gray-900">an account that could not be identified</p>
            )}
            <p className="mt-4 text-sm text-gray-600">
              This account has no access to XCentral. If you signed in with the wrong account, sign out
              and choose another. If you believe you should have access, ask a Conscierra administrator.
            </p>
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={handleSignOut}
                className="inline-flex items-center rounded-md bg-[#21362c] px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#17251e] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#21362c]"
              >
                Sign out and use another account
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
